import { useCallback, useEffect, useRef, useState } from 'react';

export type BoxModelValues = [number | undefined, number | undefined, number | undefined, number | undefined];

// expands a CSS box shorthand (1, 2, or 4 space-separated px values) into its 4 slots, following
// the same expansion rules as the CSS `padding`/`margin`/`border-radius` shorthand properties
function parseBoxShorthand(value: unknown): BoxModelValues | undefined {
  if (typeof value !== 'string') return undefined;
  const parts = value.split(' ').map((part) => parseInt(part, 10));
  if (parts.some(Number.isNaN)) return undefined;
  if (parts.length === 1) return [parts[0], parts[0], parts[0], parts[0]];
  if (parts.length === 2) return [parts[0], parts[1], parts[0], parts[1]];
  if (parts.length === 4) return [parts[0], parts[1], parts[2], parts[3]];
  return undefined;
}

function toShorthand(a: number | undefined, b: number | undefined): string | undefined {
  if (a === undefined && b === undefined) return undefined;
  return `${a ?? 0}px ${b ?? 0}px`;
}

function buildBoxShorthand([a, b, c, d]: BoxModelValues): string | undefined {
  if (a === undefined && b === undefined && c === undefined && d === undefined) return undefined;
  return `${a ?? 0}px ${b ?? 0}px ${c ?? 0}px ${d ?? 0}px`;
}

// 'all' = every slot must match to count as collapsed (corner radius' collapsed view is one value
// applied to all 4 corners); 'paired' = only opposite slots must match (padding/margin's collapsed
// view is a CSS 2-value "V H" pair, e.g. "20px 56px" - a real, common value, not "non-uniform")
type Uniformity = 'all' | 'paired';

function isUniform(parsed: BoxModelValues | undefined, mode: Uniformity): boolean {
  if (!parsed) return true;
  if (mode === 'all') return parsed[0] === parsed[1] && parsed[1] === parsed[2] && parsed[2] === parsed[3];
  return parsed[0] === parsed[2] && parsed[1] === parsed[3];
}

interface UseBoxModelStyleArgs {
  blockId: string;
  styleValue: unknown;
  resolvedValues: BoxModelValues;
  styleKey: string;
  uniformity: Uniformity;
  onUpdateStyle: (key: string, value: unknown) => void;
}

// drives one CSS box-model property (border-radius/padding/margin) for BlockStylePanel: merges
// the block's explicit override with the live-resolved DOM fallback per slot, tracks whether the
// section is expanded to its 4 independent fields, and provides the update helpers each of the
// panel's collapsed/expanded controls need
export function useBoxModelStyle({
  blockId,
  styleValue,
  resolvedValues,
  styleKey,
  uniformity,
  onUpdateStyle,
}: UseBoxModelStyleArgs) {
  const [expanded, setExpanded] = useState(false);
  const autoExpandedForBlockRef = useRef<string | null>(null);

  const explicit = parseBoxShorthand(styleValue);
  const values: BoxModelValues = [0, 1, 2, 3].map((i) => explicit?.[i] ?? resolvedValues[i]) as BoxModelValues;

  const hasExplicitValue = explicit !== undefined;
  const hasResolvedFallback = resolvedValues.some((v) => v !== undefined);

  // auto-expands off the resolved DOM value too, not just the explicit override, once either is available
  useEffect(() => {
    if (autoExpandedForBlockRef.current === blockId) return;
    if (!hasExplicitValue && !hasResolvedFallback) return;
    autoExpandedForBlockRef.current = blockId;
    setExpanded(!isUniform(explicit ?? (hasResolvedFallback ? resolvedValues : undefined), uniformity));
    // explicit/resolvedValues omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockId, hasExplicitValue, hasResolvedFallback]);

  const setSlot = useCallback(
    (index: 0 | 1 | 2 | 3, value: number | undefined) => {
      const next = [...values] as BoxModelValues;
      next[index] = value;
      onUpdateStyle(styleKey, buildBoxShorthand(next));
    },
    [values, onUpdateStyle, styleKey]
  );

  // collapsed corner-radius: one value applied uniformly to all 4 corners
  const setUniformValue = useCallback(
    (value: number | undefined) => {
      onUpdateStyle(styleKey, value === undefined ? undefined : `${value}px`);
    },
    [onUpdateStyle, styleKey]
  );

  // collapsed padding/margin: a 2-value "V H" pair - these write only 2 values (not the 4-value
  // shorthand) so an untouched axis's real value is preserved instead of getting baked in as fixed
  const setCollapsedFirst = useCallback(
    (value: number | undefined) => {
      onUpdateStyle(styleKey, toShorthand(value, values[1]));
    },
    [values, onUpdateStyle, styleKey]
  );

  const setCollapsedSecond = useCallback(
    (value: number | undefined) => {
      onUpdateStyle(styleKey, toShorthand(values[0], value));
    },
    [values, onUpdateStyle, styleKey]
  );

  return { values, expanded, setExpanded, setSlot, setUniformValue, setCollapsedFirst, setCollapsedSecond };
}
